import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  OnInit,
  afterNextRender,
  inject,
  signal,
} from '@angular/core';
import { FieldWrapper } from '@ngx-formly/core';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

/**
 * Formly wrapper that shows a field's value as plain text with a pencil icon;
 * clicking the pencil swaps in the real input.
 *
 * Meant for descriptive fields (name, description) that are set once and rarely
 * changed, so they don't take up form space on every visit.
 *
 * Behaviour:
 * - Opens straight into edit mode when the field is required and still empty
 *   (i.e. creating a new record).
 * - Collapses back to text when focus leaves the input and the value is valid.
 *   There is deliberately no "done" button: edits live in the form model
 *   immediately, so a second check/save icon would be confused with the
 *   screen's Save action.
 * - Stays open while the control is invalid and touched, so errors are visible.
 *
 * Props:
 * - `inlineEditVariant` – `'title' | 'text'`  Display style (default `'text'`).
 *
 * Usage:
 * ```typescript
 * { key: 'name', type: 'input', wrappers: ['inline-edit'], props: { label, required: true } }
 * ```
 */
@Component({
  selector: 'app-inline-edit-wrapper',
  standalone: true,
  imports: [ButtonComponent, IconComponent, TranslatePipe],
  templateUrl: './inline-edit-wrapper.component.html',
  styleUrl: './inline-edit-wrapper.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class InlineEditWrapperComponent extends FieldWrapper implements OnInit {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  /** True once the user has opened the editor (or it opened because the field is empty). */
  readonly userEditing = signal(false);

  ngOnInit(): void {
    if (this.props.required && this.isEmpty()) {
      this.userEditing.set(true);
    }
  }

  get isEditing(): boolean {
    const control = this.formControl;
    return this.userEditing() || (control.invalid && control.touched);
  }

  get displayValue(): string {
    const value = this.formControl.value;
    return value == null ? '' : String(value).trim();
  }

  get isTitle(): boolean {
    return this.props['inlineEditVariant'] === 'title';
  }

  isEmpty(): boolean {
    return this.displayValue === '';
  }

  startEditing(): void {
    this.userEditing.set(true);
    afterNextRender(
      () => {
        this.host.nativeElement
          .querySelector<HTMLElement>('input, textarea, select')
          ?.focus();
      },
      { injector: this.injector },
    );
  }

  onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (next && this.host.nativeElement.contains(next)) return;
    if (this.formControl.invalid) {
      this.formControl.markAsTouched();
      return;
    }
    this.userEditing.set(false);
  }
}
